import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { allGenres, movies, regions } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import FilterSidebar, { type FilterState } from "@/pages/category/components/FilterSidebar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import type { MovieType } from "@/types/movie";

const validTypes: MovieType[] = ["电影", "电视剧", "综艺"];

const typeDesc: Record<MovieType, string> = {
  电影: "国产在线观看电影分类页，精彩国产电影资源实时更新，方便筛选与搜索。",
  电视剧: "国产在线观看电视剧分类页，热门国产剧集资源实时更新，类型丰富随心筛选。",
  综艺: "国产在线观看综艺分类页，热播国产综艺节目实时更新，轻松解压一键直达。",
};

const defaultFilter: FilterState = {
  genres: [],
  region: "",
  yearRange: "",
  sort: "latest",
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const activeType = (validTypes.includes(type as MovieType) ? type : "电影") as MovieType;
  const isValid = validTypes.includes(type as MovieType);

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [visible, setVisible] = useState(8);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useDocumentMeta(
    `${activeType} - 国产在线观看`,
    isValid ? typeDesc[activeType] : "国产在线观看分类页，精选国产影视资源，支持多维筛选与搜索。"
  );

  const typeMovies = useMemo(() => movies.filter((m) => m.type === activeType), [activeType]);
  const genreOptions = useMemo(
    () => allGenres.filter((g) => typeMovies.some((m) => m.genres.includes(g))),
    [typeMovies]
  );

  const patchFilter = (patch: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setVisible(8);
  };

  const resetFilter = () => {
    setFilter(defaultFilter);
    setVisible(8);
  };

  const filtered = useMemo(() => {
    let list = typeMovies.filter((m) => {
      const genreOk = filter.genres.length === 0 || filter.genres.some((g) => m.genres.includes(g));
      const regionOk = !filter.region || m.region === filter.region;
      let yearOk = true;
      if (filter.yearRange) {
        const [start, end] = filter.yearRange.split("-").map(Number);
        yearOk = m.year >= start && m.year <= end;
      }
      return genreOk && regionOk && yearOk;
    });

    list = [...list].sort((a, b) => {
      if (filter.sort === "hot") return b.heat - a.heat;
      if (filter.sort === "rating") return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });

    return list;
  }, [typeMovies, filter]);

  const shown = filtered.slice(0, visible);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="text-foreground-800">{activeType}</span>
      </nav>

      <header className="mt-4">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          <i className="ri-bookmark-3-line text-primary-500" aria-hidden="true" />
          {activeType}频道
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          {typeDesc[activeType]}
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-8 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            <FilterSidebar
              genres={genreOptions}
              regions={regions}
              value={filter}
              onChange={patchFilter}
              onReset={resetFilter}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line text-base" aria-hidden="true" />
              筛选
            </button>
          </div>

          {shown.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-3 text-base font-medium text-foreground-800">暂无符合条件的影视作品</p>
              <p className="mt-1 text-sm text-foreground-500">试着调整或重置筛选条件吧</p>
              <button
                type="button"
                onClick={resetFilter}
                className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
                {shown.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              <div className="mt-8 flex justify-center">
                {visible < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + 8)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100"
                  >
                    加载更多
                    <i className="ri-arrow-down-line" aria-hidden="true" />
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已全部加载完毕</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 lg:hidden">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
            <span className="font-heading text-base font-bold text-foreground-950">筛选条件</span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="关闭筛选"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600"
            >
              <i className="ri-close-line text-2xl" aria-hidden="true" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5">
            <FilterSidebar
              genres={genreOptions}
              regions={regions}
              value={filter}
              onChange={patchFilter}
              onReset={resetFilter}
            />
          </div>
          <div className="flex gap-3 border-t border-background-200 px-4 py-3">
            <button
              type="button"
              onClick={resetFilter}
              className="flex-1 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="flex-1 whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}